import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withAPIKey } from "@aws/amazon-location-utilities-auth-helper";
import { useVehicle } from "../hooks";
import {
  LocationClient,
  CalculateRouteCommand,
  SearchPlaceIndexForPositionCommand,
} from "@aws-sdk/client-location";

export const LocationContext = React.createContext({});

const LocationProvider = ({ children }) => {
  const [locationClient, setLocationClient] = useState(null);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [departureAddress, setDepartureAddress] = useState(null);
  const [destinationAddress, setDestinationAddress] = useState(null);
  const [route, setRoute] = useState([]);
  const [direction, setDirection] = useState([]);
  const { vehicle } = useVehicle();

  const departurePosition = useMemo(
    () => [
      vehicle?.Vehicle?.Cabin?.Infotainment?.Navigation?.DeparturePoint
        ?.Longitude || 0,
      vehicle?.Vehicle?.Cabin?.Infotainment?.Navigation?.DeparturePoint
        ?.Latitude || 0,
    ],
    [
      vehicle?.Vehicle?.Cabin?.Infotainment?.Navigation?.DeparturePoint
        ?.Longitude,
      vehicle?.Vehicle?.Cabin?.Infotainment?.Navigation?.DeparturePoint
        ?.Latitude,
    ],
  );

  const currentPosition = useMemo(
    () => [
      vehicle?.Vehicle?.CurrentLocation?.Longitude || 2.3027826134953613,
      vehicle?.Vehicle?.CurrentLocation?.Latitude || 48.879692006564525,
    ],
    [
      vehicle?.Vehicle?.CurrentLocation?.Longitude,
      vehicle?.Vehicle?.CurrentLocation?.Latitude,
    ],
  );

  const destinationPosition = useMemo(
    () => [
      vehicle?.Vehicle?.Cabin?.Infotainment?.Navigation?.DestinationSet
        ?.Longitude || 0,
      vehicle?.Vehicle?.Cabin?.Infotainment?.Navigation?.DestinationSet
        ?.Latitude || 0,
    ],
    [
      vehicle?.Vehicle?.Cabin?.Infotainment?.Navigation?.DestinationSet
        ?.Longitude,
      vehicle?.Vehicle?.Cabin?.Infotainment?.Navigation?.DestinationSet
        ?.Latitude,
    ],
  );

  useEffect(() => {
    const initializeLocationClient = async () => {
      const authHelper = await withAPIKey(
        process.env.REACT_APP_LOCATION_API_KEY,
      );

      setLocationClient(
        new LocationClient({
          region: process.env.REACT_APP_LOCATION_MAP_REGION,
          credentials: { accessKeyId: "", secretAccessKey: "" },
          ...authHelper.getLocationClientConfig(),
        }),
      );
    };
    initializeLocationClient();
  }, []);

  const fetchAddressFromCoordinates = useCallback(
    async (coordinates) => {
      const response = await locationClient?.send(
        new SearchPlaceIndexForPositionCommand({
          IndexName: process.env.REACT_APP_PLACE_NAME,
          Position: coordinates,
        }),
      );

      return response?.Results?.[0]?.Place;
    },
    [locationClient],
  );

  const fetchRouteFromCoordinates = useCallback(
    async (
      departureCoordinates,
      destinationCoordinates,
      waypoints = undefined,
    ) => {
      const response = await locationClient?.send(
        new CalculateRouteCommand({
          CalculatorName: process.env.REACT_APP_LOCATION_CALCULATOR_NAME,
          DeparturePosition: departureCoordinates,
          DestinationPosition: destinationCoordinates,
          TravelMode: "Car",
          WaypointPositions: waypoints,
          IncludeLegGeometry: true,
        }),
      );

      return response?.Legs?.map((leg) => leg?.Geometry?.LineString);
    },
    [locationClient],
  );

  useEffect(() => {
    fetchAddressFromCoordinates(currentPosition).then((address) =>
      setCurrentAddress(address || { Label: "N/A" }),
    );
  }, [fetchAddressFromCoordinates, currentPosition]);

  useEffect(() => {
    fetchAddressFromCoordinates(departurePosition).then((address) =>
      setDepartureAddress(
        address || { Label: "N/A", Municipality: "N/A" },
      ),
    );
  }, [fetchAddressFromCoordinates, departurePosition]);

  useEffect(() => {
    fetchAddressFromCoordinates(destinationPosition).then((address) => {
      setDestinationAddress(
        address || { Label: "N/A", Municipality: "N/A" },
      );
    });
  }, [fetchAddressFromCoordinates, destinationPosition]);

  useEffect(() => {
    fetchRouteFromCoordinates(departurePosition, destinationPosition, [currentPosition]).then((response) => {
      setRoute(response?.[0] || []);
      setDirection(response?.[1] || []);
    }).catch((error) => {
      console.error(error);
    });
  }, [
    fetchRouteFromCoordinates,
    departurePosition,
    destinationPosition,
    currentPosition,
  ]);

  const contextValue = useMemo(
    () => ({
      currentAddress,
      departureAddress,
      destinationAddress,
      currentLatitude: currentPosition?.[1],
      currentLongitude: currentPosition?.[0],
      destinationLatitude: destinationPosition?.[1],
      destinationLongitude: destinationPosition?.[0],
      route,
      direction,
    }),
    [
      currentAddress,
      departureAddress,
      destinationAddress,
      currentPosition,
      destinationPosition,
      route,
      direction,
    ],
  );

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
};

export default React.memo(LocationProvider);
